import { request } from "@/core/lib/api-request";

/**
 * Client for the per-account Overview (`/api/admin/users/:id/analytics`).
 * Open to admins and to moderators holding the Users section; the
 * `impersonation` block comes back `null` for moderators.
 */

export const ANALYTICS_RANGES = ["7d", "30d", "90d", "all"] as const;
export type AnalyticsRange = (typeof ANALYTICS_RANGES)[number];

export type Tally = { key: string; count: number };

export type WorkspaceAnalytics = {
  ownerType: "user" | "organization";
  ownerId: string;
  name: string;
  role: string;
  billsThisAccount: boolean;
  employees: { total: number; byStatus: Tally[] };
  documents: { total: number; createdInRange: number; byStatus: Tally[] };
  signingRequests: { total: number; pending: number };
  incidents: { total: number; open: number };
  requests: { total: number; pending: number; byType: Tally[] };
  assets: number;
  policies: number;
  team: { members: number; pendingInvitations: number };
  portal: { active: number; pending: number; disabled: number };
  aiActionsInRange: number;
};

export type UserAnalytics = {
  range: AnalyticsRange;
  from: string | null;
  bucket: "day" | "month";
  credits: {
    planSlug: string;
    planName: string;
    planInterval: string | null;
    subscriptionStatus: string | null;
    monthlyAllowance: number;
    periodAllowance: number | null;
    nextResetAt: string | null;
    lastResetAt: string | null;
    balance: { total: number; subscription: number; pack: number; packsSpendable: boolean };
    usedThisPeriod: number | null;
    lifetime: { granted: number; purchased: number; consumed: number; expired: number };
    inRange: { consumed: number; granted: number; purchased: number; expired: number };
    packs: {
      id: string;
      originalCredits: number;
      remainingCredits: number;
      purchasedAt: string;
      expiresAt: string;
      expired: boolean;
    }[];
    spendByTool: {
      toolId: string | null;
      toolLabel: string;
      action: string | null;
      credits: number;
      charges: number;
    }[];
    spendOverTime: { period: string; credits: number }[];
    recentLedger: {
      id: string;
      delta: number;
      pool: string;
      reason: string;
      toolId: string | null;
      action: string | null;
      createdAt: string;
    }[];
  };
  ai: {
    actions: number;
    failures: number;
    avgLatencyMs: number | null;
    byTool: {
      toolKind: string;
      toolId: string | null;
      toolLabel: string;
      actions: number;
      failures: number;
    }[];
    byAction: Tally[];
    overTime: { period: string; actions: number }[];
  };
  totals: {
    workspaces: number;
    employees: number;
    activeEmployees: number;
    documents: number;
    documentsInRange: number;
    signingRequests: number;
    incidents: number;
    requests: number;
    assets: number;
    policies: number;
    members: number;
    pendingInvitations: number;
    portalAccounts: number;
  };
  workspaces: WorkspaceAnalytics[];
  activity: {
    signedUpAt: string;
    lastLoginAt: string | null;
    lastActiveAt: string | null;
    loginsInRange: number;
    loginsOverTime: { period: string; logins: number }[];
    actionsInRange: number;
    recent: {
      id: string;
      action: string;
      category: string;
      status: string;
      targetType: string | null;
      targetLabel: string | null;
      ownerType: string;
      ownerId: string;
      ip: string | null;
      createdAt: string;
    }[];
  };
  impersonation: {
    sessions: number;
    lastAt: string | null;
    events: {
      id: string;
      actorId: string | null;
      actorName: string | null;
      action: string;
      category: string;
      status: string;
      targetType: string | null;
      targetLabel: string | null;
      metadata: unknown;
      createdAt: string;
    }[];
  } | null;
};

export function fetchUserAnalytics(userId: string, range: AnalyticsRange): Promise<UserAnalytics> {
  return request<UserAnalytics>(
    `/api/admin/users/${encodeURIComponent(userId)}/analytics?range=${range}`,
  );
}
